import {useMemo, useState} from 'react';
import {keyBy} from 'lodash';

import {useAppDispatch} from 'stores/redux_store';
import {type Team, type TeamInvitationRole, type TeamInviteWithError} from '@mattermost/types/teams';
import {type UserProfile} from '@mattermost/types/users';
import {type Channel} from '@mattermost/types/channels';
import {Client4} from 'mattermost-redux/client';

import {type EventParametersMap, sendToStatist} from '@time-webkit/statist';

import {receivedUsers} from 'features/users';

import type {InvitationError} from './invitation_error';
import {createInvitationResult, type InvitationResult} from './invitation_result';
import {InvitationErrorCode} from './invite_members_form/invitation_error_code';
import {isAnyTeamGuestRole} from './utils';
import {handleRequestError} from './handle_request_error';
import {type ChannelFieldInvitationError} from './channel_field_invitation_error';
import {GeneralInvitationError} from './general_invitation_error';

type SendInvitesParams = {
    emails: string[];
    role: TeamInvitationRole;
    channels: Channel[];
    team: Team;
    dryRun?: boolean;
}

type State = {
    result: InvitationResult[];
    profiles: UserProfile[];
    generalError: null | GeneralInvitationError;
    success: boolean;
    errors: InvitationError[];
    channelError: ChannelFieldInvitationError | null;
    warnings: InvitationError[];
    sendingIssues: InvitationError[];
    rateLimitIssues: InvitationError[];
    criticalErrors: InvitationError[];
    remainingSendings?: number;
};

export type InviteMembersState = ReturnType<typeof useInviteMembersState>

let controller: AbortController | undefined;

const initialState: State = {
    result: [],
    profiles: [],
    errors: [],
    warnings: [],
    criticalErrors: [],
    sendingIssues: [],
    rateLimitIssues: [],
    channelError: null,
    generalError: null,
    success: false,
};

export function useInviteMembersState() {
    const errorsLog = new Set<string>();
    let uniqueErrors: Record<string, number> = {};
    let uniqueCriticalErrorsCount = 0;
    let uniqueWarningsCount = 0;

    const [state, setState] = useState<State>(initialState);
    const dispatch = useAppDispatch();

    const reset = () => {
        setState(initialState);
    };

    const profilesByEmail = useMemo(() => keyBy(state.profiles, (profile) => profile.email), [state.profiles]);
    const failedToSendInvites = useMemo(() => state.result.length > 0 && state.result.length === state.sendingIssues.length, [state.result.length, state.sendingIssues.length]);
    const rateLimitTimeoutMinutes = useMemo(() => {
        const timeoutSeconds = state.rateLimitIssues.length > 0 ? state.rateLimitIssues[0]?.retryAfterSeconds ?? 0 : 0;
        return Math.max(Math.ceil(timeoutSeconds / 60), 1);
    }, [state.rateLimitIssues]);

    const sendInvites = async (params: SendInvitesParams) => {
        const {role, emails, channels, team} = params;
        controller?.abort();
        controller = new AbortController();
        let invites: TeamInviteWithError[] = [];
        let profiles: UserProfile[] = [];
        let success = false;
        let channelError: ChannelFieldInvitationError | null = null;
        let generalError: GeneralInvitationError | null = null;

        /**
         * Если есть хоть одна крит. ошибка на форме, то принудительно выставляем dryRun в true,
         * так как бек без dryRun отправит приглашения пользователям, по которым не найдет ошибок.
         * Форма частичную отправку приглашений не поддерживает
         */
        const dryRun = Boolean(state.criticalErrors.length > 0 || params.dryRun);

        setState((prevState) => ({...prevState, generalError: null}));

        try {
            if (isAnyTeamGuestRole(role)) {
                const channelIds = channels.map((channel) => channel.id);
                const restricted = role === 'team_restricted_guest';
                invites = await Client4.sendEmailGuestInvitesToChannelsGracefully(team.id, channelIds, emails, '', dryRun, restricted, controller.signal);
            } else {
                invites = await Client4.sendEmailInvitesToTeamAndChannelsGracefully(team.id, emails, dryRun, controller.signal);
            }

            const userIds = invites.map((item) => item.user_id)
                .filter((userId): userId is string => userId !== undefined && userId !== '');

            if (userIds.length > 0) {
                profiles = await Client4.getProfilesByIds(userIds, {}, controller.signal);
                dispatch(receivedUsers(profiles));
            }
        } catch (err) {
            const errors = handleRequestError(err);

            generalError = errors.generalError;
            channelError = errors.channelError;
        }

        const profilesByEmail = keyBy(profiles, (profile) => profile.email);

        let result: InvitationResult[] = [];
        try {
            result = invites.map((invite) => createInvitationResult(invite, profilesByEmail[invite.email]));
        } catch (err) {
            generalError = new GeneralInvitationError('unknown');
        }

        const errors = result.map((result) => result.error).filter((error): error is InvitationError => error !== null);

        const criticalErrors = errors.filter((error) => error.type === 'error');

        const warnings = errors.filter((error) => error.type === 'warning');

        const sendingIssues = errors.filter((error) => error.type === 'sending_issue');

        const rateLimitIssues = errors.filter((error) => error.type === 'limit_exceeded');

        const hasCriticalErrors = criticalErrors.length > 0;

        const failedToSend = sendingIssues.length === result.length;

        const hasRateLimitIssues = rateLimitIssues.length > 0;

        const hasInvitedUsers = profiles.length > 0;

        const failedToSendDueToRateLimit = hasRateLimitIssues && !hasInvitedUsers;

        if (!dryRun && !hasCriticalErrors && !failedToSend && !failedToSendDueToRateLimit) {
            success = true;
        }

        setState((prevState) => ({
            ...prevState,
            result,
            profiles,
            errors,
            channelError,
            generalError,
            sendingIssues,
            criticalErrors,
            rateLimitIssues,
            warnings,
            success,
        }));

        (function sendStatistEvents() {
            uniqueCriticalErrorsCount = 0;
            uniqueWarningsCount = 0;
            uniqueErrors = {};

            const openChannels = channels.filter((channel) => channel.type === 'O');
            const privateChannels = channels.filter((channel) => channel.type === 'P');

            errors.forEach((error) => {
                const errorKey = JSON.stringify([error.email, error.code]);
                if (!errorsLog.has(errorKey)) {
                    if (!uniqueErrors[error.code]) {
                        uniqueErrors[error.code] = 0;
                    }
                    if (error.type === 'error') {
                        uniqueCriticalErrorsCount++;
                    } else {
                        uniqueWarningsCount++;
                    }
                    uniqueErrors[error.code]++;
                    errorsLog.add(errorKey);
                }
            });

            if (channelError) {
                uniqueCriticalErrorsCount++;
            }

            if (generalError) {
                uniqueCriticalErrorsCount++;
            }

            if (success) {
                let userRole: EventParametersMap['team.invite.send']['userRole'] = 'member';

                if (role === 'team_restricted_guest') {
                    userRole = 'guest';
                } else if (role === 'team_guest') {
                    userRole = 'guestPlus';
                }

                sendToStatist('team.invite.send', {
                    teamid: team.id,
                    userCount: profiles.length,
                    userRole,
                    channelCount: channels.length,
                    channelOpenCount: openChannels.length,
                    channelPrivateCount: privateChannels.length,
                });
            }

            if (uniqueCriticalErrorsCount > 0) {
                sendToStatist('team.invite.listError', {
                    teamid: team.id,
                    errorCount: uniqueCriticalErrorsCount,
                    emailCheck: uniqueErrors[InvitationErrorCode.API_INVALID_EMAIL] ?? 0,
                    emailLong: 0,
                    emailExist: 0,
                    userInTeam: 0,
                    userRegistered: 0,
                    userGuestOnly: uniqueErrors[InvitationErrorCode.API_ALLOWED_GUEST_PLUS_ROLE_ONLY] ?? 0,
                    userMemberOnly: uniqueErrors[InvitationErrorCode.API_ALLOWED_MEMBER_ROLE_ONLY] ?? 0,
                    userSentMember: uniqueErrors[InvitationErrorCode.API_MEMBER_ALREADY_INVITED] ?? 0,
                    userSentGuest: uniqueErrors[InvitationErrorCode.API_GUEST_PLUS_ALREADY_INVITED] ?? 0,
                });
            }
            if (uniqueWarningsCount > 0) {
                sendToStatist('team.invite.listWarning', {
                    teamid: team.id,
                    errorCount: uniqueWarningsCount,
                    userInvited: uniqueErrors[InvitationErrorCode.API_INVITATION_HAS_BEEN_SENT] ?? 0,
                });
            }
        }());
    };

    return {
        ...state,
        sendInvites,
        profilesByEmail,
        failedToSendInvites,
        rateLimitTimeoutMinutes,
        reset,
    };
}

